import { useCallback, useEffect, useState } from "react";

import type { AdminInviteFlow } from "../api";

export type PersistedInvitee = {
    id: string;
    name: string;
    email: string;
    addedAt: string;
    flow: AdminInviteFlow;
    status: string;
    message?: string;
};

const STORAGE_KEY = "atlas:admin:recentInvites";
const MAX_INVITES = 20;

const readFromStorage = (): PersistedInvitee[] => {
    if (typeof window === "undefined") {
        return [];
    }
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.filter(
            (item): item is PersistedInvitee =>
                typeof item === "object" && item !== null && typeof item.id === "string" && typeof item.email === "string" && typeof item.addedAt === "string"
        );
    } catch {
        return [];
    }
};

const writeToStorage = (invites: PersistedInvitee[]): void => {
    if (typeof window === "undefined") {
        return;
    }
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(invites));
    } catch (error) {
        console.warn("[usePersistedInvites] Failed to write to localStorage", error);
    }
};

export const usePersistedInvites = () => {
    const [invites, setInvites] = useState<PersistedInvitee[]>([]);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const stored = readFromStorage();
        setInvites(stored);
        setIsLoaded(true);
    }, []);

    useEffect(() => {
        if (isLoaded) {
            writeToStorage(invites);
        }
    }, [invites, isLoaded]);

    const addInvite = useCallback((invite: Omit<PersistedInvitee, "addedAt"> & { addedAt: Date }) => {
        const serialized: PersistedInvitee = {
            ...invite,
            addedAt: invite.addedAt.toISOString()
        };
        setInvites(prev => {
            const filtered = prev.filter(existing => existing.id !== serialized.id && existing.email !== serialized.email);
            return [serialized, ...filtered].slice(0, MAX_INVITES);
        });
    }, []);

    const clearInvites = useCallback(() => {
        setInvites([]);
    }, []);

    const removeInvite = useCallback((id: string) => {
        setInvites(prev => prev.filter(invite => invite.id !== id));
    }, []);

    return {
        invites,
        isLoaded,
        addInvite,
        clearInvites,
        removeInvite
    } as const;
};
